import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Avatar,
  ListItemAvatar,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Group as GroupIcon,
  Email,
  Security,
  People,
  Person,
  CalendarToday,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { groupService } from '../services/groupService';
import { getApiCollection, getApiData, getProp } from '../utils/apiResponse';
import { styles } from '../utils/styles';

export const GroupDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);

  const loadGroup = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await groupService.getGroupById(id!);
      const groupData = getApiData<any>(response, 'loading group details');
      setGroup(groupData);
    } catch (err: any) {
      setError(err.message || 'Failed to load group details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadMembers = useCallback(async () => {
    try {
      setMembersLoading(true);
      setMembersError(null);
      const response = await groupService.getGroupMembers(id!);
      const membersData = getApiCollection<any>(response, 'loading group members');
      setMembers(membersData);
    } catch (err: any) {
      setMembersError(err.message || 'Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      void loadGroup();
      void loadMembers();
    }
  }, [id, loadGroup, loadMembers]);

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !group) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/groups')} sx={{ mb: 2 }}>
          Back to Groups
        </Button>
        <Alert severity="error">{error || 'Group not found'}</Alert>
      </Box>
    );
  }

  const displayName = getProp(group, 'displayName');
  const description = getProp(group, 'description');
  const mail = getProp(group, 'mail');
  const mailEnabled = getProp(group, 'mailEnabled');
  const securityEnabled = getProp(group, 'securityEnabled');
  const groupTypes = getProp(group, 'groupTypes') || [];
  const createdDateTime = getProp(group, 'createdDateTime');
  const groupId = getProp(group, 'id');
  const isUnified = groupTypes.includes('Unified');
  const groupType = isUnified ? 'Microsoft 365' : securityEnabled ? 'Security' : 'Distribution';

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/groups')} sx={{ mb: 3 }}>
        Back to Groups
      </Button>

      <Box sx={styles.pageHeader}>
        <Box sx={styles.flexCenter}>
          <GroupIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">{displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {mail || 'No email address'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Edit />}>
            Edit
          </Button>
          <Button variant="outlined" color="error" startIcon={<Delete />}>
            Delete
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Status and Type */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip
                label={groupType}
                color={isUnified ? 'primary' : securityEnabled ? 'default' : 'secondary'}
              />
              {mailEnabled && <Chip label="Mail Enabled" color="info" />}
              {securityEnabled && <Chip label="Security Enabled" color="warning" />}
              <Chip
                label={`${members.length} Member${members.length !== 1 ? 's' : ''}`}
                color="success"
              />
            </Box>
          </Paper>
        </Grid>

        {/* Group Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Group Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List disablePadding>
                <ListItem disableGutters>
                  <GroupIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary="Display Name" secondary={displayName || '-'} />
                </ListItem>
                <ListItem disableGutters>
                  <Typography component="div" sx={{ mr: 2, color: 'text.secondary' }}>
                    📝
                  </Typography>
                  <ListItemText primary="Description" secondary={description || 'No description'} />
                </ListItem>
                <ListItem disableGutters>
                  <Email sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary="Email Address" secondary={mail || 'No email address'} />
                </ListItem>
                <ListItem disableGutters>
                  <Security sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary="Group Type" secondary={groupType} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Group Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Group Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List disablePadding>
                <ListItem disableGutters>
                  <ListItemText primary="Mail Enabled" secondary={mailEnabled ? 'Yes' : 'No'} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary="Security Enabled"
                    secondary={securityEnabled ? 'Yes' : 'No'}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <CalendarToday sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText
                    primary="Created Date"
                    secondary={
                      createdDateTime ? new Date(createdDateTime).toLocaleDateString() : '-'
                    }
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Group ID" secondary={groupId || '-'} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Group Members */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  <People sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Members ({members.length})
                </Typography>
                <Button variant="outlined" size="small" startIcon={<Person />}>
                  Add Member
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {membersError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {membersError}
                </Alert>
              )}
              {membersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : members.length > 0 ? (
                <List>
                  {members.map((member: any, index: number) => {
                    const memberDisplayName = getProp(member, 'displayName');
                    const memberUPN = getProp(member, 'userPrincipalName');
                    const memberMail = getProp(member, 'mail');
                    const memberId = getProp(member, 'id');

                    return (
                      <ListItem
                        key={memberId || index}
                        sx={{
                          borderBottom: index < members.length - 1 ? '1px solid #e0e0e0' : 'none',
                          '&:hover': { backgroundColor: '#f5f5f5' },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <Person />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={memberDisplayName || memberId || 'Unknown Member'}
                          secondary={
                            <Box component="span">
                              {memberUPN && (
                                <Box component="span" sx={{ display: 'block' }}>
                                  {memberUPN}
                                </Box>
                              )}
                              {memberMail && memberMail !== memberUPN && (
                                <Box
                                  component="span"
                                  sx={{ display: 'block', color: 'text.secondary' }}
                                >
                                  {memberMail}
                                </Box>
                              )}
                            </Box>
                          }
                        />
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            // Handle remove member
                          }}
                        >
                          Remove
                        </Button>
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography color="text.secondary">
                  No members were returned for this group.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
